<template>
    <Scaffold>
        <Loader :loading="saving || loading"></Loader>
        <div id="component-form">
            <header>
                <h2>{{ label }}</h2>
                <span>{{ trads.subhead.interpolate({ brand: brand.name }) }}</span>
            </header>
            <FormHead v-model="headData" :createMode="!data" />
            <FormDefaultCampaign @changeCategories="changeCategories" v-if="showCampaignStep" v-model="defaultCampaignData" />
            <FormReferences
                v-if="headData.componentType"
                :key="headData.componentType"
                v-model="references"
                :componentType="headData.componentType"
            />
            <p v-if="clickedOnSaveAtLeastOnce && !canSave" class="error">{{ trads.missingProperties }}</p>
            <footer>
                <ClientColorButton :class-name="'back'" :inactive-color="true" @click.prevent="$emit('goBack');" v-if="data">
                    {{ $trad.common.cancel }}
                </ClientColorButton>
                <div>
                    <ClientColorButton
                        :disabled="clickedOnSaveAtLeastOnce && !canSave"
                        @click="canSave ? save() : (clickedOnSaveAtLeastOnce = true);"
                    >
                        {{ $trad.common.save }}
                    </ClientColorButton>
                    <ClientColorButton :inactive-color="true" @click="reset">{{ $trad.common.reset }}</ClientColorButton>
                </div>
            </footer>
        </div>
    </Scaffold>
</template>
<script>
import Scaffold from "@/app-scaffold";
import { mapGetters } from "vuex";
import FormHead from "./form-head";
import FormDefaultCampaign from "./form-default-campaign.vue";
import FormReferences from "./form-references";
import { isNotEmpty } from "@/utils/string";
import ClientColorButton from "@/components/ui/ClientColorButton";
import { ELEMENT_AVAILABILITY, COMPONENT_TYPE, ESTORE_AVAILABILITY } from "@/services/component";
import { TrySaveMixin } from "@/mixins/TryLoadMixin";
import Loader from "@/components/loader/loader";
import component from "@/services/component";

function hasAtLeastOneLang(trads) {
    return trads && Object.keys(trads).length > 0 && Object.keys(trads).some(locale => isNotEmpty(trads[locale]));
}

export default {
    name: "ComponentForm",
    mixins: [TrySaveMixin],
    components: { Scaffold, FormHead, FormDefaultCampaign, FormReferences, ClientColorButton, Loader },
    props: ["label", "data", "loading"],
    data: () => ({
        headData: {},
        defaultCampaignData: {},
        references: {},
        clickedOnSaveAtLeastOnce: false,
        cogsProperty: null
    }),
    watch: {
        async data(value) {
            function deserializeTranslation(trad) {
                const res = {};
                for (const key in trad) {
                    res[key] = trad[key].value;
                }
                return res;
            }

            this.references = {};
            try {
                this.cogsProperty = await component.cogs(this.$route.params.id);
            } catch (e) {
                console.error(e);
            }
            if (value.infosArticle) {
                for (const key in value.infosArticle) {
                    this.references[value.infosArticle[key].id] = value.infosArticle[key].componentProperty.value;
                }
                if (this.cogsProperty) {
                    this.references[this.cogsProperty.type] = this.cogsProperty.value;
                }
            }

            // prevent watchers from reseting the datas.
            this.abortResets = true;

            let selectedAvailableOn = [];
            if (value.elementPlano) selectedAvailableOn.push(ELEMENT_AVAILABILITY.planogram);
            if (value.elementCarcass) selectedAvailableOn.push(ELEMENT_AVAILABILITY.carcass);
            if (value.elementCluster) selectedAvailableOn.push(ELEMENT_AVAILABILITY.template);

            this.headData = {
                componentType: COMPONENT_TYPE[value.type.name],
                name: deserializeTranslation(value.terms.name),
                nickname: deserializeTranslation(value.terms.nickname),
                standDesignId: (value.standDesign && value.standDesign.id) || undefined,
                selectedSystemTypes: value.systemTypes.map(systemType => systemType.id),
                selectedAvailableOn,
                visible: value.visible,
                toValidate: value.toValidate,
                isValidate: value.isValidate
            };

            const zoneDetailsPerId = {};
            for (const zone of value.zones) {
                if (!zone.zone) continue;
                zoneDetailsPerId[zone.zone.id] = {
                    checked: true,
                    startCampaignId: zone.campaign && zone.campaign.id,
                    endCampaignId: zone.discontinuedCampaign && zone.discontinuedCampaign.id
                };
            }

            this.defaultCampaignData = {
                ...this.defaultCampaignData,
                startCampaignId: (value.campaigns.launch && value.campaigns.launch.id) || undefined,
                endCampaignId: (value.campaigns.discontinued && value.campaigns.discontinued.id) || undefined,
                zoneDetailsPerId,
                description: deserializeTranslation(value.terms.description),
                statusId: (value.status && value.status.id) || undefined,
                categories: value.categories.map(details => {
                    let ids = [];
                    let cat = details.category;
                    while (cat) {
                        ids.push(cat.id);
                        cat = cat.parent;
                    }
                    return ids.reverse();
                }),
                eStoreData: {
                    eStoreType: value.eStoreType || ESTORE_AVAILABILITY.unavailable,
                    prices: value.prices
                }
            };
        },
        "headData.componentType"() {
            if (this.abortResets) {
                // other watchers may want to reset something too,
                // so we can only disable the flag after the end of the frame
                setTimeout(() => (this.abortResets = false));
                return;
            }
            this.headData.selectedSystemTypes = [];
            this.headData.selectedAvailableOn = [];
            this.references = {};
        }
    },
    computed: {
        ...mapGetters({ brand: "brand/getBrand" }),
        trads() {
            return this.$trad.admin.componentForm;
        },
        showCampaignStep() {
            return (
                this.headData.componentType &&
                (this.headData.componentType === COMPONENT_TYPE.product ||
                    !this.hasAccessApplication("plano_builder") ||
                    (this.headData.selectedAvailableOn.length > 0 && this.headData.selectedSystemTypes.length > 0)) &&
                hasAtLeastOneLang(this.headData.name) &&
                hasAtLeastOneLang(this.headData.nickname)
            );
        },
        canSave() {
            const hasAtLeastOneZone = Object.values(this.defaultCampaignData.zoneDetailsPerId).some(details => {
                return details.checked;
            });

            const hasAtLeastOneCategory = this.defaultCampaignData.categories.length > 0;

            return this.showCampaignStep && this.defaultCampaignData.startCampaignId && hasAtLeastOneZone && hasAtLeastOneCategory;
        }
    },
    methods: {
        flushForm() {
            this.headData = this.emptyHeadData();
            this.defaultCampaignData = this.emptyCampaignData();
            this.references = {};
        },
        save() {
            this.$emit("save", {
                ...this.headData,
                ...this.defaultCampaignData,
                references: this.references,
                brandId: this.brand.id
            });
        },
        reset() {
            (this.data && this.$emit("reset")) || this.flushForm();
        },
        changeCategories(newCategories) {
            this.defaultCampaignData.categories = [...newCategories]; // pour le re-render
        },
        emptyCampaignData() {
            return {
                description: {},
                startCampaignId: undefined,
                endCampaignId: undefined,
                zoneDetailsPerId: {},
                statusId: undefined,
                categories: [{}],
                eStoreData: {
                    eStoreType: undefined,
                    prices: []
                }
            };
        },
        emptyHeadData() {
            return {
                componentType: undefined,
                name: {},
                nickname: {},
                selectedSystemTypes: [],
                selectedAvailableOn: [],
                standDesign: { id: null }
            };
        }
    },
    created() {
        this.headData = this.emptyHeadData();
        this.defaultCampaignData = this.emptyCampaignData();
    }
};
</script>
<style lang="scss">
#main #component-form {
    width: 767px;
    margin: 40px auto;

    @media (max-width: 767px) {
        width: calc(100% - 60px);
    }

    > header {
        margin-bottom: 30px;
        h2 {
            margin: 10px 0;
        }
    }

    > section {
        margin-top: 30px;
    }

    ul {
        margin: 0;
        padding: 0;
        > li {
            list-style-type: none;
        }
    }

    section {
        display: grid;
        grid-template-columns: auto 1fr;
        margin-bottom: 30px;
        grid-column-gap: 20px;
        grid-row-gap: 15px;

        @media (min-width: 767px) {
            align-items: start;
            justify-items: start;
        }

        @media (max-width: 767px) {
            grid-row-gap: 4px;
            grid-template-columns: 1fr;

            > :not(label) {
                margin: 0 15px;
            }
            label {
                &:not(:first-of-type) {
                    margin-top: 15px;
                }
            }
        }
    }

    label {
        text-transform: uppercase;
        font-weight: bold;

        &[required]::after {
            content: " *";
            color: red;
        }
    }

    > .error {
        color: red;
        font-weight: bold;
    }
    footer {
        display: grid;
        grid-template-columns: auto auto;
        grid-gap: 20px;
        justify-content: space-between;
    }
}
</style>



// WEBPACK FOOTER //
// component-form.vue?d55718ce